// src/Dashboard/pages/user/DashboardUser/DashboardUser.jsx

import React, { useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useUser } from "../../../../pages/Home/UserContext";
import { getRoleFromRoleId } from "../../../../utils/roleHelper";
import useScrollToTop from "../../../../hooks/useScollToTop";

import useDashboardUserData from "./hooks/useDashboardUserData";

import DashboardUserHeader from "./components/DashboardUserHeader";
import DashboardUserModuleNav from "./components/DashboardUserModuleNav";
import DashboardUserSectionShell from "./components/DashboardUserSectionShell";
import PendingPaymentsPanel from "./components/PendingPaymentsPanel";
import MessagesPanel from "./components/MessagesPanel";
import NotificationsPanel from "./components/NotificationsPanel";
import ObjectifsPanel from "./components/ObjectifsPanel";
import CompetitionsPanel from "./components/CompetitionsPanel";
import FavoriteEventsPanel from "./components/FavoriteEventsPanel";
import FavoriteTechniquesPanel from "./components/FavoriteTechniquesPanel";
import ContactFormPanel from "./components/ContactFormPanel";

export default function DashboardUser() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const {
    userId,
    active,
    setActive,
    counts,
    modules,
    isAdminOrTeacher,
    onCountsPayments,
    onCountsMessages,
    onCountsNotifications,
    onCountsObjectifs,
    onCountsCompetitions,
    onCountsEvents,
    onCountsTechniques,
    onCountsContact,
  } = useDashboardUserData({ user, userRole });

  useEffect(() => {
    if (!userId) {
      toast.error("Vous devez être connecté pour accéder au tableau de bord.");
    }
  }, [userId]);

  const activeModule =
    modules.find((module) => module.id === active) || modules[0];

  if (!userId) {
    return (
      <main className="w-full p-4 sm:p-6 lg:p-8">
        <div className="rounded-[28px] border border-slate-200 bg-white/80 p-10 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900/70">
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">
            Connexion nécessaire
          </h1>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Vous devez être connecté pour accéder à cette page.
          </p>
        </div>

        <ToastContainer />
      </main>
    );
  }

  return (
    <main className="w-full p-4 sm:p-6 lg:p-8">
      <div className="mx-auto w-full max-w-7xl">
        <DashboardUserHeader user={user} counts={counts} />

        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[18rem,1fr] lg:gap-6">
          <DashboardUserModuleNav
            items={modules}
            activeId={active}
            onChange={setActive}
          />

          <div className="min-w-0">
            {active === "payments" && (
              <DashboardUserSectionShell
                title="Règlements à effectuer"
                subtitle="Retrouvez les commandes, inscriptions ou cotisations encore en attente de paiement."
                icon={activeModule?.icon}
              >
                <PendingPaymentsPanel
                  userId={userId}
                  onCounts={onCountsPayments}
                />
              </DashboardUserSectionShell>
            )}

            {active === "messages" && (
              <DashboardUserSectionShell
                title="Messages"
                subtitle="Consultez vos messages reçus. Les détails s’ouvrent dans un panneau latéral."
                icon={activeModule?.icon}
              >
                <MessagesPanel userId={userId} onCounts={onCountsMessages} />
              </DashboardUserSectionShell>
            )}

            {active === "notifications" && (
              <DashboardUserSectionShell
                title="Notifications"
                subtitle="Toutes les informations importantes envoyées par le club."
                icon={activeModule?.icon}
              >
                <NotificationsPanel
                  userId={userId}
                  onCounts={onCountsNotifications}
                />
              </DashboardUserSectionShell>
            )}

            {active === "objectifs" && (
              <DashboardUserSectionShell
                title="Objectifs de saison"
                subtitle="Définissez et suivez les objectifs personnels des adhérents liés à votre compte."
                icon={activeModule?.icon}
              >
                <ObjectifsPanel
                  userId={userId}
                  onCounts={onCountsObjectifs}
                />
              </DashboardUserSectionShell>
            )}

            {active === "competitions" && (
              <DashboardUserSectionShell
                title="Inscriptions & résultats"
                subtitle="Visualisez les évènements auxquels vos adhérents sont inscrits, les statuts et les résultats."
                icon={activeModule?.icon}
              >
                <CompetitionsPanel
                  userId={userId}
                  onCounts={onCountsCompetitions}
                />
              </DashboardUserSectionShell>
            )}

            {active === "events" && (
              <DashboardUserSectionShell
                title="Évènements favoris"
                subtitle="Les évènements que vous avez enregistrés depuis le calendrier."
                icon={activeModule?.icon}
              >
                <FavoriteEventsPanel userId={userId} onCounts={onCountsEvents} />
              </DashboardUserSectionShell>
            )}

            {active === "techniques" && (
              <DashboardUserSectionShell
                title="Techniques favorites"
                subtitle="Les techniques ajoutées en favoris dans la bibliothèque technique."
                icon={activeModule?.icon}
              >
                <FavoriteTechniquesPanel
                  userId={userId}
                  onCounts={onCountsTechniques}
                />
              </DashboardUserSectionShell>
            )}

            {active === "contact" && isAdminOrTeacher && (
              <DashboardUserSectionShell
                title="Formulaires de contact"
                subtitle="Messages envoyés depuis le formulaire public du site."
                icon={activeModule?.icon}
              >
                <ContactFormPanel userId={userId} onCounts={onCountsContact} />
              </DashboardUserSectionShell>
            )}
          </div>
        </div>
      </div>

      <ToastContainer />
    </main>
  );
}